import React, { useMemo } from "react";
import {
  Box,
  Chip,
  Switch,
  Typography,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { MapTimelineDriverRow } from "app/types/mapScreen";

const LEGEND_ITEMS = [
  { color: "#2563eb", label: "Assigned" },
  { color: "#38bdf8", label: "Loaded" },
  { color: "#f59e0b", label: "Pending" },
  { color: "#ef4444", label: "Late" },
  { color: "#ec4899", label: "Failed" },
  { color: "#22c55e", label: "Done" },
  { color: "#94a3b8", label: "Unassigned" },
];

type MapTimelinePanelProps = {
  rows: MapTimelineDriverRow[];
  onFocusRoute?: (routeId: number) => void;
};

function buildHourLabels(startHour: number, endHour: number): number[] {
  const labels: number[] = [];
  for (let hour = startHour; hour <= endHour; hour += 1) {
    labels.push(hour);
  }
  return labels.length > 0 ? labels : [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
}

export function MapTimelinePanel({
  rows,
  onFocusRoute,
}: MapTimelinePanelProps) {
  const hourRange = useMemo(() => {
    if (rows.length === 0) {
      return buildHourLabels(16, 23);
    }
    const start = Math.min(...rows.map((row) => row.startHour));
    const end = Math.max(...rows.map((row) => row.endHour));
    return buildHourLabels(start, end);
  }, [rows]);

  const currentHour = new Date().getHours();
  const timelineStart = hourRange[0] ?? 16;
  const timelineEnd = hourRange[hourRange.length - 1] ?? 23;
  const timelineSpan = Math.max(timelineEnd - timelineStart, 1);
  const currentPosition =
    ((Math.min(Math.max(currentHour, timelineStart), timelineEnd) - timelineStart) /
      timelineSpan) *
    100;

  return (
    <Box
      sx={{
        borderTop: "1px solid #e5e9ef",
        bgcolor: "#fff",
        minHeight: 320,
        maxHeight: 420,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          borderBottom: "1px solid #eef1f4",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: "0.82rem", color: "#334155", fontWeight: 600 }}>
            Driver timelines
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <FormControlLabel
            control={<Switch size="small" defaultChecked />}
            label={
              <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>
                Enable Live ETA
              </Typography>
            }
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography sx={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600 }}>
              Legend
            </Typography>
            {LEGEND_ITEMS.map((item) => (
              <Box
                key={item.label}
                title={item.label}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: item.color,
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto" }}>
        {rows.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              No driver timelines for the selected date.
            </Typography>
          </Box>
        ) : (
          rows.map((row) => (
              <Box
                key={`${row.id}-${row.routeId}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "220px 1fr 40px",
                  alignItems: "center",
                  borderBottom: "1px solid #f1f5f9",
                  minHeight: 72,
                }}
              >
                <Box sx={{ px: 2, py: 1.25 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.88rem" }}>
                      {row.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${row.jobCount} jobs`}
                      sx={{
                        height: 22,
                        fontSize: "0.68rem",
                        bgcolor: row.hasAlert ? "#fef3c7" : "#f1f5f9",
                        color: row.hasAlert ? "#b45309" : "#475569",
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                    {row.vehicleType} · {row.routeName}
                  </Typography>
                </Box>

                <Box sx={{ position: "relative", px: 1, py: 2, minWidth: 640 }}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 24,
                      right: 24,
                      display: "grid",
                      gridTemplateColumns: `repeat(${hourRange.length}, 1fr)`,
                    }}
                  >
                    {hourRange.map((hour) => (
                      <Typography
                        key={hour}
                        sx={{
                          fontSize: "0.68rem",
                          color: "#94a3b8",
                          textAlign: "center",
                        }}
                      >
                        {String(hour).padStart(2, "0")}:00
                      </Typography>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      mt: 2.5,
                      height: 28,
                      borderRadius: "999px",
                      bgcolor: "#f1f5f9",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: -8,
                        bottom: -8,
                        left: `${currentPosition}%`,
                        width: "2px",
                        bgcolor: "#5fa67a",
                        zIndex: 2,
                      }}
                    />
                    {row.stops.map((stop, index) => {
                      const offset =
                        row.stops.length <= 1
                          ? 50
                          : (index / Math.max(row.stops.length - 1, 1)) * 100;
                      return (
                        <Box
                          key={stop.id}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: `${Math.min(Math.max(offset, 4), 96)}%`,
                            transform: "translate(-50%, -50%)",
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            border: "2px solid #ef4444",
                            bgcolor: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            color: "#ef4444",
                          }}
                        >
                          {stop.label}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <SearchIcon
                    sx={{
                      fontSize: 18,
                      color: "#64748b",
                      cursor: row.routeId ? "pointer" : "default",
                    }}
                    onClick={() => row.routeId && onFocusRoute?.(row.routeId)}
                  />
                </Box>
              </Box>
            ))
        )}
      </Box>
    </Box>
  );
}
